import {
  ElementKind,
  type Baseline,
  type Project,
} from '@shared/models/project.model';
import type { OneOfChildFactoryElements } from '@shared/models/factory-element.model';
import { addElements } from '../../project.helpers';
import {
  findElement,
  isOneOfParentElements,
} from '../../recursive-element.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { asManualCopy } from '../../element-copy.helpers';

export type ElementAddChange = ProjectChangeMap[ProjectChangeType.ElementAdd];

/**
 * Build the {@link IAddElementOptions} payload for {@link addElements} from an
 * {@link ElementAddChange}.
 */
const elementAddOptionsFromChange = (
  change: ElementAddChange,
): Parameters<typeof addElements>[2] => ({
  placement: change.placement,
  siblingId: change.siblingId,
  regenerateIds: change.regenerateIds,
  generateUniqueName: change.generateUniqueName,
});

/**
 * The factory to add. A duplicate (`duplicateSubtreeSourceElementId`) is a
 * manual copy, so the source's provenance — import origin, proposal ownership —
 * is dropped from the whole subtree (DEV-3702, see {@link asManualCopy}). The
 * change shape carries a partial factory; `addElements` fills in the missing fields.
 */
const elementAddFactoryFromChange = (
  change: ElementAddChange,
): OneOfChildFactoryElements => {
  const factory = change.factory as OneOfChildFactoryElements;
  const copy = change.duplicateSubtreeSourceElementId
    ? asManualCopy(factory)
    : factory;
  // Stamp proposal ownership last so it survives the copy strip above.
  return change.createdInProposalId && copy.kind === ElementKind.Element
    ? { ...copy, createdInProposalId: change.createdInProposalId }
    : copy;
};

/**
 * Apply a single {@link ProjectChangeType.ElementAdd} change to a project by delegating
 * to {@link addElements} so placement, sibling positioning, name dedup and proposal selection
 * remap stay in one place.
 *
 * Returns the input project unchanged when the parent cannot be found or when it is not a
 * valid container (e.g. a product element); the change is then dropped silently.
 */
export const applyElementAddChange = (
  project: Project,
  change: ElementAddChange,
): Project => {
  const parent = findElement(project, change.elementId);
  if (!isOneOfParentElements(parent)) return project;

  const { root } = addElements(
    project,
    parent,
    elementAddOptionsFromChange(change),
    elementAddFactoryFromChange(change),
  );
  return root;
};

/**
 * Same as {@link applyElementAddChange} but scoped to a single {@link Baseline} root —
 * used by the version-only pipeline path.
 */
export const applyElementAddChangeToVersion = (
  version: Baseline,
  change: ElementAddChange,
): Baseline => {
  const parent = findElement(version, change.elementId);
  if (!isOneOfParentElements(parent)) return version;

  const { root } = addElements(
    version,
    parent,
    elementAddOptionsFromChange(change),
    elementAddFactoryFromChange(change),
  );
  return root;
};
